import React from 'react';
import styled from 'styled-components';
import { Text } from '@wegroup/design-system';

// Components
import { H4 } from 'wg-fe-ui';
import RadioButton from './RadioButton';

interface Props {
  Icon: React.ReactElement;
  title: string;
  description?: string;
  onClick: () => void;
  isSelected: boolean;
}

const RadioInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  Icon,
  title,
  description,
  isSelected,
  onClick,
  ...otherProps
}) => {
  return (
    <Wrapper
      onClick={() => onClick()}
      isSelected={isSelected}
      {...otherProps}
      data-test-id="ProdFac_RadioInputComponent"
    >
      <IconContainer
        isSelected={isSelected}
        data-test-id="ProdFac_RadioInputComponent_Icon"
      >
        {Icon}
      </IconContainer>
      <TextWrapper data-test-id="ProdFac_RadioInputComponent_TextContainer">
        <Title data-test-id="ProdFac_RadioInputComponent_Title">{title}</Title>
        {description && (
          <Text
            textStyle="smallBody"
            color="text.gray"
            data-test-id="ProdFac_RadioInputComponent_Description"
          >
            {description}
          </Text>
        )}
      </TextWrapper>
      <RadioButton
        isSelected={isSelected}
        data-test-id="ProdFac_RadioInputComponent_RadioButton"
      />
    </Wrapper>
  );
};

const IconContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.primary[50] : theme.gray[50]};
  border-radius: 100%;
  width: 4rem;
  height: 4rem;
  padding: 0.75rem;

  svg {
    path {
      fill: ${({ isSelected, theme }) =>
        isSelected ? theme.primary[500] : theme.gray[300]};
    }
  }
`;

const TextWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled(H4)`
  font-weight: 400;
  line-height: 120%;
  text-align: left;
`;

const Wrapper = styled.div<{ isSelected: boolean; prefilled?: boolean }>`
  background: #fff;
  display: flex;
  align-items: center;
  padding: ${({ isSelected }) =>
    isSelected ? 'calc(1.5rem - 1px)' : '1.5rem'};
  border: ${({ isSelected, theme }) =>
    isSelected
      ? `2px solid ${theme.primary[500]}`
      : `1px solid ${theme.gray[50]}`};
  border-radius: 8px;
  cursor: pointer;
  width: 100%;

  & > * {
    margin-right: 2rem;

    &:last-child {
      margin-right: 0;
    }
  }

  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.02), 0 0 2px rgba(0, 0, 0, 0.03),
      0 0 1px rgba(0, 0, 0, 0.02);
  }
`;

export default RadioInputComponent;
